import React, { useMemo, useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeSlug from 'rehype-slug';
import Layout from '../components/Layout';
import TableOfContents from '../components/TableOfContents';
import { gospelContent, workContent, familyContent } from '../content/about';
import { extractToc } from '../utils/extractToc';
import { setupThemeListener } from '../utils/highlightTheme';
import '../styles/markdown.css';

const About = () => {
  const [mobileIsOpen, setMobileIsOpen] = useState(false);

  // Extract TOC from all content sections
  const toc = useMemo(() => {
    const combinedContent = `## The Gospel\n\n${gospelContent}\n\n## Family\n\n${familyContent}\n\n## Work\n\n${workContent}`;
    return extractToc(combinedContent);
  }, []);

  // Set up dynamic syntax highlighting theme
  useEffect(() => {
    const cleanup = setupThemeListener();
    return cleanup;
  }, []);

  const handleToggleToc = () => {
    setMobileIsOpen(!mobileIsOpen);
  };

  return (
    <Layout showTocButton={true} onToggleToc={handleToggleToc}>
      <TableOfContents toc={toc} mobileIsOpen={mobileIsOpen} onMobileToggle={handleToggleToc} />

      <div id="content" className="max-w-5xl mx-auto px-4 md:px-8 xl:px-24 2xl:px-32">
        <div className="mb-12">
          <h1 className="text-4xl tracking-tight text-black dark:text-gray-200 sm:text-5xl">Core Beliefs</h1>
        </div>

        <section id="gospel" className="mb-8 scroll-mt-16 md:mb-12 lg:mb-16 lg:scroll-mt-24" aria-label="The Gospel">
          <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-white/75 dark:bg-gray-900/75 px-6 py-5 backdrop-blur md:-mx-12 md:px-12 lg:relative lg:top-auto lg:w-full">
            <h2 className="text-xl font-bold uppercase tracking-widest text-black dark:text-gray-200">The Gospel</h2>
          </div>
          <div className="prose dark:prose-invert prose-lg max-w-none">
            <div className="markdown-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSlug, rehypeHighlight]}
              >
                {gospelContent}
              </ReactMarkdown>
            </div>
          </div>
        </section>

        <section id="family" className="mb-8 scroll-mt-16 md:mb-12 lg:mb-16 lg:scroll-mt-24" aria-label="Family">
          <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-white/75 dark:bg-gray-900/75 px-6 py-5 backdrop-blur md:-mx-12 md:px-12 lg:relative lg:top-auto lg:w-full">
            <h2 className="text-xl font-bold uppercase tracking-widest text-black dark:text-gray-200">Family</h2>
          </div>
          <div className="prose dark:prose-invert prose-lg max-w-none">
            <div className="markdown-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSlug, rehypeHighlight]}
              >
                {familyContent}
              </ReactMarkdown>
            </div>
          </div>
        </section>

        <section id="work" className="mb-8 scroll-mt-16 md:mb-12 lg:mb-16 lg:scroll-mt-24" aria-label="Work">
          <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-white/75 dark:bg-gray-900/75 px-6 py-5 backdrop-blur md:-mx-12 md:px-12 lg:relative lg:top-auto lg:w-full">
            <h2 className="text-xl font-bold uppercase tracking-widest text-black dark:text-gray-200">Work</h2>
          </div>
          <div className="prose dark:prose-invert prose-lg max-w-none">
            <div className="markdown-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSlug, rehypeHighlight]}
              >
                {workContent}
              </ReactMarkdown>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default About;
